import React, { useState, useEffect, useReducer, useContext, useRef, useCallback, useMemo } from "react";

import { makeStyles } from "@material-ui/core/styles";
import List from "@material-ui/core/List";
import Paper from "@material-ui/core/Paper";
import InboxOutlinedIcon from "@material-ui/icons/InboxOutlined";
import { useQueryClient } from "react-query";

import TicketListItem from "../TicketListItemCustom";
import TicketsListSkeleton from "../TicketsListSkeleton";

import useTickets from "../../hooks/useTickets";
import { i18n } from "../../translate/i18n";
import { AuthContext } from "../../context/Auth/AuthContext";
import { SocketContext } from "../../context/Socket/SocketContext";

const useStyles = makeStyles((theme) => ({
  ticketsListWrapper: {
    position: "relative",
    display: "flex",
    height: "100%",
    flexDirection: "column",
    overflow: "hidden",
    borderTopRightRadius: 0,
    borderBottomRightRadius: 0,
    backgroundColor: theme.palette.type === "dark" ? "#131614" : "#ffffff",
    // Ensure scrollbar has room to display properly
    paddingLeft: theme.direction === "rtl" ? 4 : 0,
    paddingRight: theme.direction === "rtl" ? 0 : 4,
  },

  ticketsList: {
    flex: 1,
    maxHeight: "100%",
    overflowY: "auto",
    // End of the list must NOT chain the scroll into the page.
    overscrollBehavior: "contain",
    overflowX: "hidden",
    // Brand-dark token (#0F1210, same as Tasks/Settings/Chat) for
    // dark mode. The old #0f172a was slate-navy and read as a stray
    // blue panel inside the otherwise brand-green dark theme.
    backgroundColor: theme.palette.type === "dark" ? "#0F1210" : "#f8fafc",
    padding: "8px 12px",
    // Ensure scrollbar has proper spacing in RTL mode
    marginLeft: theme.direction === "rtl" ? 0 : 0,
    marginRight: theme.direction === "rtl" ? 0 : 0,
    ...theme.scrollbarStyles,
    // Fix scrollbar track background for clean edges
    "&::-webkit-scrollbar-track": {
      backgroundColor: theme.palette.type === "dark" ? "#0F1210" : "#f1f5f9",
      borderRadius: 8,
      margin: "8px 0",
    },
  },

  ticketsListHeader: {
    color: theme.palette.type === "dark" ? "rgba(255,255,255,0.8)" : "rgb(67, 83, 105)",
    zIndex: 2,
    backgroundColor: theme.palette.type === "dark" ? "#131614" : "#ffffff",
    borderBottom: theme.palette.type === "dark" 
      ? "1px solid rgba(255,255,255,0.08)" 
      : "1px solid rgba(0, 0, 0, 0.12)",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
  },

  ticketsCount: {
    fontWeight: "normal",
    color: theme.palette.type === "dark" ? "rgba(255,255,255,0.6)" : "rgb(104, 121, 146)",
    marginLeft: "8px",
    fontSize: "14px",
  },

  // Subtle entry for the empty-state container — fades in instead
  // of popping after the tickets list resolves.
  "@keyframes emptyStateFadeIn": {
    from: { opacity: 0, transform: "translateY(6px)" },
    to: { opacity: 1, transform: "translateY(0)" },
  },
  noTicketsText: {
    textAlign: "center",
    color: theme.palette.type === "dark" ? "rgba(255,255,255,0.5)" : "rgb(104, 121, 146)",
    fontSize: "14px",
    lineHeight: "1.5",
    maxWidth: 260,
    margin: "8px auto 0",
  },

  noTicketsTitle: {
    textAlign: "center",
    fontSize: "16px",
    fontWeight: 600,
    margin: 0,
    color: theme.palette.type === "dark" ? "rgba(255,255,255,0.85)" : "#374151",
  },

  noTicketsDiv: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    padding: "48px 24px",
    margin: "0 auto",
    animation: "$emptyStateFadeIn 0.3s ease-out",
  },

  noTicketsIconWrap: {
    width: 56,
    height: 56,
    borderRadius: 16,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    // Brand-green icon pill (matches Tasks empty states / FieldCard
    // section headers). Old indigo→purple gradient was leftover
    // generic Tailwind palette — off-brand.
    background:
      theme.palette.type === "dark"
        ? "linear-gradient(135deg, rgba(46,227,122,0.18), rgba(18,140,126,0.16))"
        : "linear-gradient(135deg, rgba(37,211,102,0.14), rgba(18,140,126,0.10))",
    color: theme.palette.type === "dark" ? "#7df0a8" : "#128c7e",
    marginBottom: 14,
    "& svg": { fontSize: 28 },
  },
}));

// ─── IMMUTABILITY RULE ────────────────────────────────────────────────────────
// React's useReducer (and StrictMode's double-invoke in dev) requires reducers
// to be pure functions. Every case MUST return a new array/object — never
// mutate the `state` argument in place. The symptoms of impurity:
//   • React StrictMode / Concurrent Mode runs the reducer twice to detect
//     side-effects. A mutating reducer sees the already-mutated state on the
//     second run → duplicate rows (e.g. navigation back to a panel doubles
//     the ticket count because page-2 append ran twice on the same array).
//   • Stale closures that hold a reference to the old `state` array see
//     mutations they didn't expect, causing subtle UI flicker.
// All cases below operate on a fresh copy and return it. `state` is never
// written to directly.
// ─────────────────────────────────────────────────────────────────────────────
const reducer = (state, action) => {
  if (action.type === "LOAD_TICKETS") {
    // Per Raviv's 2026-05-13 audit: the old reducer always merged
    // newTickets INTO state — existing IDs got an in-place update,
    // new IDs got `state.push(ticket)`. Two regressions came out
    // of that pattern once we had a socket-driven DELETE flow:
    //
    //   1. Ghost tickets: anything DELETE_TICKET removed from
    //      state would silently reappear the next time react-query
    //      re-served the page (e.g. on filter change, where
    //      keepPreviousData briefly returned the cached set).
    //
    //   2. Wrong order: new entries went to the BOTTOM of state
    //      via push() instead of respecting the server's
    //      ORDER BY updatedAt DESC.
    //
    // Fix: split the load by page number.
    //   - Page 1 = fresh server snapshot → full replace, no
    //     legacy state survives. Ghosts gone, order preserved.
    //   - Page 2+ = pagination append, deduplicated by id so the
    //     same ticket doesn't double-render if it bridged two
    //     pages.
    const { tickets: newTickets, pageNumber } = action.payload;

    if (pageNumber === 1) {
      return [...newTickets];
    }

    // Pure append: build a new array from state + only the IDs we
    // don't already have. No mutation of the `state` reference.
    const existingIds = new Set(state.map((t) => t.id));
    const toAdd = newTickets.filter((t) => !existingIds.has(t.id));
    return toAdd.length > 0 ? [...state, ...toAdd] : state;
  }

  if (action.type === "RESET_UNREAD") {
    const ticketId = action.payload;
    // map() returns a new array; the matched item gets a new object.
    return state.map((t) =>
      t.id === ticketId ? { ...t, unreadMessages: 0 } : t
    );
  }

  if (action.type === "UPDATE_TICKET") {
    const ticket = action.payload;

    const ticketIndex = state.findIndex((t) => t.id === ticket.id);
    if (ticketIndex !== -1) {
      const existing = state[ticketIndex];
      const merged = {
        ...existing,
        ...ticket,
        // Preserve resolved associations when the IDs still match —
        // avoids re-fetching queue/contact objects on every socket ping.
        queue:
          existing.queueId === ticket.queueId && existing.queue
            ? existing.queue
            : ticket.queue,
        contact:
          existing.contactId === ticket.contactId && existing.contact
            ? existing.contact
            : ticket.contact,
        // Preserve unreadMessages — only RESET_UNREAD and
        // UPDATE_TICKET_UNREAD_MESSAGES should touch it.
        unreadMessages: existing.unreadMessages,
      };
      // Status / assignee / tag changes should bubble the ticket to
      // the top of the list — backend always touches `updatedAt` on a
      // mutation so the initial /tickets fetch (ORDER BY updatedAt
      // DESC) puts it first; this mirrors that for live socket
      // updates.
      const rest = state.filter((_, i) => i !== ticketIndex);
      return [merged, ...rest];
    }
    return [ticket, ...state];
  }

  if (action.type === "UPDATE_TICKET_UNREAD_MESSAGES") {
    // Fired when a REAL new customer message arrives — bubble ticket to top.
    const ticket = action.payload;

    const ticketIndex = state.findIndex((t) => t.id === ticket.id);
    if (ticketIndex !== -1) {
      const existing = state[ticketIndex];
      const merged = {
        ...existing,
        ...ticket,
        // Guard (Raviv 2026-06-07): if the socket payload reports
        // unreadMessages = 0 OR omits the field entirely — usually because
        // the ticket owner is currently viewing it, or because an emitter
        // sent a stub `ticket: { id }` — don't wipe the badge for OTHER
        // viewers (e.g. an admin watching the same list, or a custom-section
        // tab) who haven't read it yet. Clearing the badge here was a real
        // bug for tickets moved Pending → Custom while a new message
        // arrives: the badge briefly appeared then disappeared. Only
        // RESET_UNREAD is allowed to genuinely zero the badge — that action
        // fires via the `updateUnread` socket event after the local user
        // actually opens the ticket. `undefined > 0` is false → fallback
        // also covers the stub case.
        unreadMessages: ticket.unreadMessages > 0
          ? ticket.unreadMessages
          : existing.unreadMessages,
        queue:
          existing.queueId === ticket.queueId && existing.queue
            ? existing.queue
            : ticket.queue,
        contact:
          existing.contactId === ticket.contactId && existing.contact
            ? existing.contact
            : ticket.contact,
      };
      const rest = state.filter((_, i) => i !== ticketIndex);
      return [merged, ...rest];
    }
    return [ticket, ...state];
  }

  if (action.type === "MARK_TICKET_UNREAD") {
    // Fired when an agent manually marks a ticket as unread (envelope button).
    // ONLY sets unreadMessages = 1 — position is preserved.
    // A 3-day-old ticket stays where it is, just gets the yellow badge.
    const ticket = action.payload;
    return state.map((t) =>
      t.id === ticket.id ? { ...t, unreadMessages: 1 } : t
    );
  }

  if (action.type === "UPDATE_TICKET_CONTACT") {
    const contact = action.payload;
    return state.map((t) =>
      t.contactId === contact.id ? { ...t, contact } : t
    );
  }

  if (action.type === "UPDATE_TICKET_PRESENCE") {
    const { ticketId, presence } = action.payload;
    return state.map((t) =>
      t.id === ticketId ? { ...t, presence } : t
    );
  }

  if (action.type === "DELETE_TICKET") {
    return state.filter((t) => t.id !== action.payload);
  }

  // Bulk eviction. Fires when the sections list changes (a new
  // tag-filter section is created elsewhere) and the panel needs
  // to drop every ticket that's now claimed by another section.
  // See the "Reconcile panel state with the latest sections list"
  // effect in TicketsListCustom for the rationale.
  if (action.type === "PRUNE_TICKETS") {
    const idsToDrop = new Set(action.payload || []);
    if (idsToDrop.size === 0) return state;
    return state.filter((t) => !idsToDrop.has(t.id));
  }

  if (action.type === "RESET") {
    return [];
  }
};

const TicketsListCustom = (props) => {
  const {
    status,
    groups,
    isSearch,
    searchParam,
    setStatusParam,
    tags,
    users,
    whatsapps,
    showAll,
    selectedQueueIds,
    updateCount,
    style,
    setTabOpen,
    showTabGroups,
    sectionId,
    sections,
    channel,
  } = props;
  const classes = useStyles();
  const [pageNumber, setPageNumber] = useState(1);
  const [update, setUpdate] = useState(0);
  const [ticketsList, dispatch] = useReducer(reducer, []);
  const [ticketsListUpdated, setTicketsListUpdated] = useState([]);
  const { user } = useContext(AuthContext);
  const queryClient = useQueryClient();
  const { profile, queues } = user;

  const socketManager = useContext(SocketContext);

  // Ref-mirror of the current ticketsList ids. The socket handler
  // captures this ref at bind time, then reads the LIVE set during
  // every `update` event — so we can tell "this ticket is already
  // in our state vs. the socket wants us to inject a new row" even
  // when the reducer state has moved on. Used by belongsHere() to
  // avoid pulling tickets into the wrong panel when the socket
  // payload doesn't carry the `tags` association (claim unknown).
  const stateIdsRef = useRef(new Set());
  useEffect(() => {
    stateIdsRef.current = new Set(ticketsList.map((t) => t.id));
  }, [ticketsList]);

  // Ref-mirror of the sections prop. The socket effect closure reads
  // sectionsRef.current so it always has the LATEST sections list
  // without capturing a stale snapshot. This lets us remove `sections`
  // from the socket useEffect dependency array — if `sections` were in
  // the deps, any parent re-render that passes a new array reference
  // (even with identical content) would tear down and re-register all
  // socket listeners, emitting spurious joinTickets/leaveTickets pairs
  // and causing a brief reconnect flash on every navigation.
  const sectionsRef = useRef(sections);
  useEffect(() => {
    sectionsRef.current = sections;
  }, [sections]);

  // Serialize array props to stable strings so the RESET effect only fires
  // when the VALUES change, not when the parent re-renders with new array
  // references that have identical content (e.g. tags=[] vs tags=[]).
  // useTickets already does the same JSON.stringify for the same reason.
  const tagsKey = JSON.stringify(tags);
  const usersKey = JSON.stringify(users);
  const whatsappsKey = JSON.stringify(whatsapps);
  const queueIdsKey = JSON.stringify(selectedQueueIds);

  useEffect(() => {
    dispatch({ type: "RESET" });
    setPageNumber(1);
  // 2026-06-11: `channel` added — switching the channel filter must reset to
  // page 1, else the new-channel page-N rows dedup-append onto stale rows.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status, searchParam, showAll, tagsKey, usersKey, whatsappsKey, queueIdsKey, sectionId, channel]);

  // Stable fingerprint of the sections list. Mixed into the useTickets
  // cache key (NOT sent to the API) so any change to the sections
  // collection — creation, deletion, reordering — busts the cache and
  // triggers a fresh /tickets fetch. This is the bypass for the bug
  // Raviv hit after section deletion: useTickets has no idea about
  // sections, so without this its cache key stays identical and
  // refetchQueries from the parent effect couldn't replace the stale
  // data (the in-flight fetch hit the same cache slot and react-query
  // sometimes returned the prior data on the next render). Including
  // the section ids in the key forces a brand-new cache entry on every
  // sections mutation, sidestepping that race entirely.
  const sectionsFingerprint = useMemo(() => {
    if (!Array.isArray(sections)) return "0";
    return sections.map((s) => s?.id ?? "").join(",");
  }, [sections]);

  const { tickets, hasMore, loading, initialLoading } = useTickets({
    pageNumber,
    isSearch,
    searchParam,
    status,
    groups,
    showAll,
    tags: JSON.stringify(tags),
    users: JSON.stringify(users),
    whatsapps: JSON.stringify(whatsapps),
    queueIds: JSON.stringify(selectedQueueIds),
    sectionId,
    channel,
    cacheSalt: sectionsFingerprint,
  });

  useEffect(() => {
    // Page number is part of the payload so the reducer can decide
    // between "full replace" (page 1 = fresh snapshot from the
    // server, discards any ghost state) and "dedup append" (page 2+
    // = pagination growth). See the LOAD_TICKETS case in the
    // reducer for the full rationale (Raviv 2026-05-13).
    // Only skip the stale-cache dispatch when the reducer is already
    // populated. On a fresh mount the reducer starts at [] — showing
    // 0 tickets while waiting for the background refetch is worse UX
    // than briefly showing the cached data. Once the reducer has data
    // we skip the stale dispatch to prevent overwriting socket-driven
    // changes (ghost tickets / position resets).
    const isStaleCache =
      loading && !initialLoading && pageNumber === 1 && tickets.length > 0
      && stateIdsRef.current.size > 0;
    if (isStaleCache) return;
    dispatch({ type: "LOAD_TICKETS", payload: { tickets, pageNumber } });
  }, [tickets, queues, profile, pageNumber, loading, initialLoading]);

  // Reconcile panel state with the latest sections list. Without
  // this, creating a new tag-filter section in another tab / by
  // another admin left the existing tickets in our panel state
  // un-revisited: the socket-handler closure that runs `belongsHere`
  // captured a stale `sections` array, and `resolveClaimedSectionId`
  // returned null for tickets that should now be claimed by the
  // newly-created section. Result was the "ticket lives in Open
  // AND in the new custom tab" duplicate Raviv reproduced on staging.
  // Move-to never showed the bug because that path mutates a ticket
  // the agent is acting on right now (so the next individual update
  // event covers it); section-create is the race that doesn't.
  // Reducer can't reach `sections` from module scope, so do the
  // resolution here in component scope where the latest prop is
  // visible, then dispatch a bulk PRUNE with the ids to drop.
  useEffect(() => {
    if (ticketsList.length === 0) return;
    const tagFilterSections = (sections || []).filter(
      (s) => s && s.type === "tag-filter" && Array.isArray(s.tagIds) && s.tagIds.length > 0
    );
    const sortedSections = [...tagFilterSections].sort(
      (a, b) => (a.position - b.position) || (a.id - b.id)
    );
    const claimFor = (ticket) => {
      if (!ticket) return undefined;
      // Keep in lock-step with resolveClaimedSectionId: a CLOSED ticket is
      // claimed by NO section, so the prune evicts it from custom panels and it
      // lives in the Closed tab. (Raviv 2026-06.) The backend still LISTS closed
      // tickets in the section, so REST pagination is intact — they're pruned
      // from display here, so the 2026-05-15 "stuck at ~6" regression does NOT
      // return.
      if (ticket.status === "closed") return null;
      if (!Array.isArray(ticket.tags)) return undefined;
      const ticketTagIds = ticket.tags.map((t) => t?.id).filter((id) => id != null);
      if (ticketTagIds.length === 0) return null;
      if (sortedSections.length === 0) return null;
      const tagSet = new Set(ticketTagIds);
      for (const s of sortedSections) {
        if (s.tagIds.some((id) => tagSet.has(id))) return s.id;
      }
      return null;
    };
    const orphanIds = [];
    for (const t of ticketsList) {
      const claim = claimFor(t);
      if (claim === undefined) continue; // tags missing on this row — leave alone
      if (sectionId) {
        if (claim !== sectionId) orphanIds.push(t.id);
      } else {
        if (claim !== null) orphanIds.push(t.id);
      }
    }
    if (orphanIds.length > 0) {
      dispatch({ type: "PRUNE_TICKETS", payload: orphanIds });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sections, sectionId]);

  // Section deletion previously needed an imperative refetch effect
  // here. That's now handled structurally via the `cacheSalt` arg to
  // useTickets (sectionsFingerprint mixes into the query key), so a
  // sections-list mutation produces a fresh react-query cache entry
  // and the fetch fires automatically. The legacy `dispatch RESET`
  // path was racing with LOAD_TICKETS — the new fetch's data landed,
  // LOAD_TICKETS populated state, then RESET ran AFTERWARDS and wiped
  // it (Raviv 2026-05-13: ticket badge correct, list empty until
  // page refresh). Removing the imperative path resolves that race.

  useEffect(() => {
    const companyId = localStorage.getItem("companyId");
    const socket = socketManager.GetSocket(companyId);

    const shouldUpdateTicket = (ticket) => {
      if (!ticket) return false;
      // Supervisor sees all tickets just like admin (showAll is already "true"
      // for supervisors from the server fetch, but the local shouldUpdateTicket
      // guard also needs to pass so real-time socket updates aren't dropped).
      const isAdmin = profile === "admin" || profile === "supervisor";
      const userMatch = !ticket.userId || Number(ticket.userId) === Number(user?.id) || showAll || isAdmin;
      // Admin/supervisor bypass queue filter — they see all queues regardless
      // of which queues are currently selected in the UI filter strip.
      const queueMatch = isAdmin || !ticket.queueId || selectedQueueIds.indexOf(ticket.queueId) > -1 || selectedQueueIds.indexOf(Number(ticket.queueId)) > -1;
      return userMatch && queueMatch;
    };

    // Mirror of backend ResolveSectionMembership for socket-driven
    // updates. The initial /tickets fetch already excludes claimed
    // tickets from system tabs (and constrains custom-section views
    // to their primary bucket), but socket events bypass that path
    // and would otherwise inject the ticket into every panel whose
    // status matches. Without this, a ticket with the "Customer
    // Closed" tag still leaks into Open the moment a new message
    // arrives — exactly the bug Raviv reported.
    //
    // Rule: lowest position wins, tie-break on lowest id. A ticket
    // with no matching tag is "unclaimed" and belongs to system tabs.
    // Returns:
    //   undefined → can't tell (the socket payload didn't include tags)
    //   null      → ticket carries no tag that matches any custom section
    //   <id>      → ticket is claimed by the section with that id
    //
    // The "undefined" case is critical: appMessage emits from message
    // create paths sometimes ship a partial Ticket object without the
    // tag association loaded. Treating that as "definitely unclaimed"
    // would evict the ticket from its custom section the moment its
    // owner sends a reply (Raviv's bug — chat disappears from a custom
    // tab until reload). We propagate the unknown upwards so the
    // dispatcher can keep the ticket where it already is.
    const resolveClaimedSectionId = (ticket) => {
      if (!ticket) return undefined;
      // Closed tickets belong to the Closed system tab, not a custom section.
      // (Raviv 2026-06: closing from a custom tab like "agents" must MOVE the
      // ticket to Closed.) Returning null routes a just-closed ticket into the
      // Closed tab and evicts it from the section LIVE. The backend section LIST
      // still returns closed tickets, so REST pagination/hasMore is unchanged
      // (the 2026-05-15 "stuck at ~6" regression does NOT return) — the closed
      // rows are simply pruned from the section's display here.
      if (ticket.status === "closed") return null;
      if (!Array.isArray(ticket.tags)) return undefined;
      const ticketTagIds = ticket.tags.map(t => t?.id).filter(id => id != null);
      if (ticketTagIds.length === 0) return null;
      const tagFilterSections = (sectionsRef.current || []).filter(
        s => s && s.type === "tag-filter" && Array.isArray(s.tagIds) && s.tagIds.length > 0
      );
      if (tagFilterSections.length === 0) return null;
      const sorted = [...tagFilterSections].sort((a, b) =>
        (a.position - b.position) || (a.id - b.id)
      );
      const tagSet = new Set(ticketTagIds);
      for (const s of sorted) {
        if (s.tagIds.some(id => tagSet.has(id))) return s.id;
      }
      return null;
    };

    // True iff the ticket should live in *this* panel.
    //   - custom section panel (sectionId set): claim must match
    //   - system panel (no sectionId): claim must be null
    //   - if claim is unknown (tags missing from the payload):
    //       * if the ticket is ALREADY in this panel's state →
    //         keep it (defensive, avoid flashing on partial
    //         payloads);
    //       * if it ISN'T in state yet → don't pull it in. With a
    //         backend emit that strips the tags association
    //         (e.g. AIAgentEngine.reload({include:["contact","queue"]})),
    //         the "unknown" path was injecting the same ticket
    //         into every panel because belongsHere always said yes
    //         (Raviv 2026-05-13: ticket showed in both Open and
    //         the custom "test" section simultaneously, vanished
    //         after refresh because the REST endpoint scopes
    //         correctly server-side).
    const belongsHere = (ticket) => {
      const claimed = resolveClaimedSectionId(ticket);
      if (claimed === undefined) {
        return stateIdsRef.current.has(ticket?.id);
      }
      if (sectionId) return claimed === sectionId;
      return claimed === null;
    };

    // Double indexOf mirrors the existing fix in shouldUpdateTicket: the
    // socket payload can return queueId as a string while selectedQueueIds
    // holds numbers. Both checks are needed so strict === inside indexOf
    // doesn't produce a false-positive eviction.
    const notBelongsToUserQueues = (ticket) =>
      ticket.queueId &&
      selectedQueueIds.indexOf(ticket.queueId) === -1 &&
      selectedQueueIds.indexOf(Number(ticket.queueId)) === -1;

    const onConnectTicketList = () => {
      // joinTickets / joinNotification are NOT re-emitted here:
      // Initial mount is handled by the direct emit below.
      // Reconnect is handled by ManagedSocket.refreshJoinsOnReady.
      // Emitting here caused double-emit on every mount (socketReady=true
      // fires onConnect immediately), leaking server-side CounterManager
      // so leaveTickets never decremented back to zero.
      queryClient.invalidateQueries(["tickets"]);
    }
    
    const onCompanyTicket = (data) => {
      
      if (data.action === "updateUnread") {
        dispatch({
          type: "RESET_UNREAD",
          payload: data.ticketId,
        });
        return;
      }

      // updateUnreadMark: agent clicked "Mark as unread" — show yellow badge
      // WITHOUT moving the ticket. Uses MARK_TICKET_UNREAD (not
      // UPDATE_TICKET_UNREAD_MESSAGES) so the ticket stays in its current
      // position. Only a real incoming customer message should bubble to top.
      //
      // Guard: only dispatch if this ticket is already in THIS panel's state.
      // Without the guard every panel (open / pending / closed / custom) receives
      // the same socket event and each one inserts the row into every tab.
      if (data.action === "updateUnreadMark") {
        if (data.ticket && stateIdsRef.current.has(data.ticket.id)) {
          dispatch({
            type: "MARK_TICKET_UNREAD",
            payload: data.ticket,
          });
        }
        return;
      }

      if (data.action === "update") {
        // Early return if no ticket data
        if (!data.ticket) return;
        
        const shouldUpdate = shouldUpdateTicket(data.ticket);
        const statusMatch = data.ticket?.status === status;
        
        // If ticket no longer belongs to user's queues - remove it
        if (notBelongsToUserQueues(data.ticket)) {
          dispatch({ type: "DELETE_TICKET", payload: data.ticket?.id });
          return;
        }

        // Section-claim guard: a ticket that's claimed by a custom
        // section must not show up in any system panel, and a custom
        // section panel must only display tickets it owns. If the
        // ticket no longer belongs here, evict it (covers the case
        // where a tag was just added and the ticket was previously
        // visible in this panel).
        if (!belongsHere(data.ticket)) {
          dispatch({ type: "DELETE_TICKET", payload: data.ticket?.id });
          return;
        }

        // Custom-section panel (no status prop): closed tickets are
        // already evicted above by belongsHere() — resolveClaimedSectionId
        // returns null for status="closed", so claim !== sectionId and the
        // early DELETE_TICKET path runs. Everything that reaches here is
        // a live (open/pending) ticket that we still own, so just keep
        // the row in sync.
        if (status === undefined) {
          if (shouldUpdateTicket(data.ticket)) {
            dispatch({
              type: "UPDATE_TICKET",
              payload: data.ticket,
            });
          }
        }
        // System panel: keep iff status matches the tab, evict otherwise.
        else if (data.ticket?.status === status && shouldUpdateTicket(data.ticket)) {
          dispatch({
            type: "UPDATE_TICKET",
            payload: data.ticket,
          });
        }
        else if (data.ticket?.status !== status) {
          dispatch({ type: "DELETE_TICKET", payload: data.ticket?.id });
        }
        // Handle groups tab — else if prevents double dispatch: without it a
        // group ticket that already matched the statusMatch branch above would
        // fire UPDATE_TICKET a second time here, producing duplicate row moves.
        else if (groups && data.ticket?.isGroup && shouldUpdateTicket(data.ticket)) {
          dispatch({
            type: "UPDATE_TICKET",
            payload: data.ticket,
          });
        }
      }

      if (data.action === "delete") {
        dispatch({ type: "DELETE_TICKET", payload: data?.ticketId });
        
      }

      if (data.action === "removeFromList") {
        dispatch({ type: "DELETE_TICKET", payload: data.ticketId });
      }

    }
    
    const onCompanyAppMessage = (data) => {
      
      // Early return if no ticket data
      if (!data.ticket) {
        return;
      }
      
      if (showTabGroups && !!data.ticket?.isGroup !== !!groups) {
        return;
      }

      const queueIds = queues.map((q) => q.id);
      // Only filter by queue if ticket HAS a queue assigned
      // Tickets without a queue should be visible to all users
      if (
        profile === "user" &&
        data.ticket?.queue &&  // Only check if ticket has a queue
        queueIds.indexOf(data.ticket.queue.id) === -1
      ) {
        return;
      }

      const shouldUpdate = shouldUpdateTicket(data.ticket);
      const statusMatch = status === undefined || data.ticket?.status === status;
      const groupMatch = !showTabGroups || !!data.ticket?.isGroup === !!groups;
      

      if (
        data.action === "create" &&
        groupMatch &&
        shouldUpdate &&
        statusMatch &&
        belongsHere(data.ticket)
      ) {
        dispatch({
          type: "UPDATE_TICKET_UNREAD_MESSAGES",
          payload: data.ticket,
        });
      }
    }

	const onCompanyContact = (data) => {
      if (data.action === "update") {
        dispatch({
          type: "UPDATE_TICKET_CONTACT",
          payload: data.contact,
        });
      }
    }
    
	// Emit the room join IMMEDIATELY in addition to the onConnect
	// callback. The callback only fires if `socketReady` flips from
	// false → true after registration; if the socket "ready" event
	// already fired before the panel mounted (which is the common
	// case for /tickets navigation — socket is established at app
	// boot), the onConnect handler is never invoked and the panel
	// never joins its status room. Result on Raviv's staging probe:
	// the Open panel was receiving `updateUnread` events (those are
	// broadcast to the auto-joined `company-N-mainchannel` room) but
	// NOT `update` events (those go to `company-N-open` which the
	// panel never joined). Tag-mutation updates never reached Open,
	// so the ticket stayed in both old and new tab. Fire immediately
	// here so the room is joined the moment the listener is wired,
	// keep the onConnect callback as a re-join hook for the
	// reconnect path.
	if (status) {
		socket.emit("joinTickets", status);
	} else {
		socket.emit("joinNotification");
	}
	socketManager.onConnect(onConnectTicketList);

    socket.on(`company-${companyId}-ticket`, onCompanyTicket);
    socket.on(`company-${companyId}-appMessage`, onCompanyAppMessage);
    socket.on(`company-${companyId}-contact`, onCompanyContact );
    
    const onCompanyPresence = (data) => {
      dispatch({
        type: "UPDATE_TICKET_PRESENCE",
        payload: data,
      });
    };
    
    socket.on(`company-${companyId}-presence`, onCompanyPresence);

    return () => {
      if (status) {
        socket.emit("leaveTickets", status);
      } else {
        socket.emit("leaveNotification");
      }
      // Remove specific listeners instead of disconnecting entire socket
      socket.off(`company-${companyId}-ticket`, onCompanyTicket);
      socket.off(`company-${companyId}-appMessage`, onCompanyAppMessage);
      socket.off(`company-${companyId}-contact`, onCompanyContact);
      socket.off(`company-${companyId}-presence`, onCompanyPresence);
      // socketManager.onConnect delegates to socket.on("connect", ...).
      // Without this removal the reconnect handler accumulates on every
      // re-render, causing duplicate room-joins on every socket reconnect.
      socket.off("connect", onConnectTicketList);
    };
    
  // `sections` is intentionally omitted — the socket handler reads
  // sectionsRef.current so it always has the latest list without
  // triggering a reconnect when the parent passes a new array reference
  // with identical content. `sectionId` stays because it identifies
  // which panel this is (custom vs system) and changes mean a new view.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status, showAll, groups, showTabGroups, user, selectedQueueIds, tags, users, profile, queues, socketManager, sectionId]);

  useEffect(() => {
    if (typeof updateCount === "function") {
      updateCount(ticketsList.length);
    }
    // Track length, not the array reference — reducer returns a new
    // [...state] on every dispatch (including no-op socket updates),
    // which previously fired this effect on every event and stormed
    // /tickets/counters until the browser hit ERR_INSUFFICIENT_RESOURCES.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ticketsList.length]);


  const isFetchingRef = useRef(false);
  // Mirror hasMore / loading into refs so handleScroll reads the LIVE
  // values instead of the closure snapshot. Raviv 2026-05-14 reported
  // custom-tab Closed pagination cutting off — that signature matches
  // a stale-closure miss: useCallback bound the handler with
  // [hasMore, loading] deps, so a fast scroll burst that crossed the
  // trigger zone while loading was mid-toggle could re-enter with the
  // prior false value, advance pageNumber, then never re-fire because
  // hasMore had become false in the captured snapshot.
  const hasMoreRef = useRef(hasMore);
  const loadingRef = useRef(loading);
  useEffect(() => {
    hasMoreRef.current = hasMore;
    loadingRef.current = loading;
  }, [hasMore, loading]);

  // Release the in-flight lock when the fetch actually settles, not on
  // an arbitrary 300ms timer. The previous implementation cleared
  // isFetchingRef.current after a fixed setTimeout — if the backend
  // responded slower than 300ms (or the panel was racing other panels
  // for the network) the lock dropped before the new page was in
  // state, and a subsequent scroll-near-bottom event triggered ANOTHER
  // pageNumber++ before the prior one resolved. Tying the unlock to
  // `loading` flipping false guarantees we wait for the real cycle.
  useEffect(() => {
    if (!loading) isFetchingRef.current = false;
  }, [loading]);

  // rAF-throttled scroll handle. Guard order matters: check the cheap
  // ref flags (isFetching / hasMore / loading) BEFORE touching
  // scrollHeight/clientHeight — those layout reads force a synchronous
  // reflow, and on a socket-mutated list this was firing on every
  // single scroll tick even when a fetch was already in flight or
  // pagination was exhausted. Deferring the read into a rAF also
  // collapses a fast scroll burst (many `scroll` events per frame)
  // down to one layout read per paint.
  const scrollRafRef = useRef(null);
  const handleScroll = useCallback((e) => {
    if (isFetchingRef.current) return;
    if (!hasMoreRef.current) return;
    if (loadingRef.current) return;
    if (scrollRafRef.current) return;
    const target = e.currentTarget;
    scrollRafRef.current = requestAnimationFrame(() => {
      scrollRafRef.current = null;
      // Re-check refs — state may have changed while this frame was queued.
      if (isFetchingRef.current) return;
      if (!hasMoreRef.current) return;
      if (loadingRef.current) return;
      const { scrollTop, scrollHeight, clientHeight } = target;
      // Tightened trigger zone from 150 -> 100 so the threshold can be
      // uncrossed on initial mount of a short list. With 150, a short
      // custom-section panel was already INSIDE the zone at first
      // render — there was no "scroll-into" event to fire on, so the
      // handler never tripped on subsequent scrolls.
      if (scrollHeight - scrollTop > clientHeight + 100) return;
      isFetchingRef.current = true;
      setPageNumber(prev => prev + 1);
    });
  }, []);

  useEffect(() => {
    return () => {
      if (scrollRafRef.current) cancelAnimationFrame(scrollRafRef.current);
    };
  }, []);

  return (
    <Paper className={classes.ticketsListWrapper} style={style}>
      <Paper
        square
        name="closed"
        elevation={0}
        className={classes.ticketsList}
        onScroll={handleScroll}
      >
        <List style={{ paddingTop: 0 }}>
          {ticketsList.length === 0 && !loading ? (
            <div className={classes.noTicketsDiv}>
              <div className={classes.noTicketsIconWrap}>
                <InboxOutlinedIcon />
              </div>
              <span className={classes.noTicketsTitle}>
                {i18n.t("ticketsList.noTicketsTitle")}
              </span>
              <p className={classes.noTicketsText}>
                {i18n.t("ticketsList.noTicketsMessage")}
              </p>
            </div>
          ) : (
            <>
              {ticketsList.map((ticket) => (
                <TicketListItem
                  ticket={ticket}
                  setTabOpen={setTabOpen}
                  key={ticket.id}
                  groupActionButtons={!groups && !showTabGroups}
                  setStatusParam={setStatusParam}
                />
              ))}
            </>
          )}
          {/* Show skeleton ONLY on first load (no cached data yet).
              `loading` (= isFetching) flips true on every background
              refetch — including the one React-Query kicks off after
              a click ripple — which made the list flash a skeleton
              row every time the user picked a ticket. */}
          {(initialLoading || (loading && ticketsList.length === 0)) && <TicketsListSkeleton />}
        </List>
      </Paper>
    </Paper>
  );
};

// The parent re-renders on every counts poll — memo keeps the 5 always-mounted panels out of that churn.
export default React.memo(TicketsListCustom);
